import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { formatDateInput, parseDateInput } from '../../utils/dateInputUtils';

/**
 * Tum uygulamada kullanilan ortak tarih secici.
 *
 * Native <input type="date"> yerine kullanilir: Chrome/Safari/Firefox'ta ayni
 * takvimi render eder ve secilen tarih her tarayicida temizlenebilir.
 *
 * Deger `yyyy-MM-dd` string'i olarak alinir/verilir; boylece native input
 * kullanan ekranlarda state ve API payload'lari degismeden gecis yapilabilir.
 */
export interface AppDatePickerProps {
  label?: string;
  /** `yyyy-MM-dd` formatinda tarih; bos string = secim yok */
  value: string;
  /** `yyyy-MM-dd` formatinda tarih doner; temizlendiginde bos string */
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  /** `yyyy-MM-dd` */
  minDate?: string;
  /** `yyyy-MM-dd` */
  maxDate?: string;
  /** Bugunden sonrasi secilemez (geriye donuk islem tarihleri icin) */
  disableFuture?: boolean;
  /** Bugunden oncesi secilemez */
  disablePast?: boolean;
  /** Temizleme (X) butonu; varsayilan acik */
  clearable?: boolean;
  sx?: SxProps<Theme>;
  helperText?: string;
  error?: boolean;
  name?: string;
  id?: string;
  autoFocus?: boolean;
  className?: string;
}

export const AppDatePicker: React.FC<AppDatePickerProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  fullWidth = false,
  size = 'small',
  variant = 'outlined',
  minDate,
  maxDate,
  disableFuture = false,
  disablePast = false,
  clearable = true,
  sx,
  helperText,
  error,
  name,
  id,
  autoFocus,
  className,
}) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const displayFormat = isEnglish ? 'MM/dd/yyyy' : 'dd.MM.yyyy';

  return (
    <DatePicker
      label={label}
      format={displayFormat}
      value={parseDateInput(value)}
      disabled={disabled}
      minDate={minDate ? parseDateInput(minDate) ?? undefined : undefined}
      maxDate={maxDate ? parseDateInput(maxDate) ?? undefined : undefined}
      disableFuture={disableFuture}
      disablePast={disablePast}
      onChange={(newValue) => onChange(formatDateInput(newValue))}
      slotProps={{
        field: { clearable: clearable && !disabled },
        // Takvim acikken de temizleyebilmek icin (ozellikle dokunmatik cihazlar)
        actionBar: {
          actions: clearable ? ['clear', 'today', 'accept'] : ['today', 'accept'],
        },
        textField: {
          size,
          variant,
          fullWidth,
          required,
          disabled,
          helperText,
          error,
          name,
          id,
          autoFocus,
          className,
          sx,
        },
      }}
    />
  );
};

export default AppDatePicker;
